import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Play,
  RotateCcw,
  Check,
  ArrowRight,
  User,
  Camera,
  Video,
  ShieldCheck,
  Info,
  CircleCheck,
  Link2,
  MapPin,
  Heart,
  FileText,
} from "lucide-react";
import { IMAGES } from "@/data/content";
import { useNoIndex } from "@/lib/useNoIndex";
import { useReelTimeline } from "@/components/demo/useReelTimeline";
import { DemoPlayerBar } from "@/components/demo/DemoPlayerBar";
import { DemoEndSlate } from "@/components/DemoEndSlate";

/**
 * DemoStory — 60-second auto-playing walkthrough of the student experience.
 *
 * Route: /demo/story
 *
 * Each act is a screen-fidelity mock of the *actual* student-facing UI
 * (CreateCampaign wizard + published CampaignDetail page), with fields
 * filling themselves in as time advances. Framed inside a "browser
 * window" chrome so a board room reads it as: "here's what students see
 * when they use the platform, sped up 20x."
 *
 * Acts:
 *   01  Personal details (fields typing in, .edu badge popping)
 *   02  Profile photo, gallery photos, and video pitch
 *   03  Funding need — policy strip, goal, category, story, docs
 *   04  Attestations — 3 required checkboxes tick themselves
 *   05  Verified & live — the published campaign page as donors see it
 *   06  End slate — yellow watermark, "aunties and uncles, paying it forward"
 */

const TOTAL_SECONDS = 60;

const ACTS = [
  {
    id: "01",
    startAt: 0,
    duration: 12,
    kicker: "step 1  ·  personal details",
    title: "The student fills out their profile.",
    subtitle:
      "Name, school, major, email address, graduation year.",
  },
  {
    id: "02",
    startAt: 12,
    duration: 10,
    kicker: "step 2  ·  photo + video pitch",
    title: "Photos and a short video.",
    subtitle:
      "The moments donors connect with, in the student's own voice.",
  },
  {
    id: "03",
    startAt: 22,
    duration: 12,
    kicker: "step 3  ·  funding need",
    title: "Goal, category, and the story behind it.",
    subtitle:
      "This is where students make their requests and tell their stories.",
  },
  {
    id: "04",
    startAt: 34,
    duration: 10,
    kicker: "step 4  ·  attestations",
    title: "Three legally required attestations.",
    subtitle:
      "Confirm eligibility requirements satisfied. Consent to enrollment verification. Promise that funds will be used for approved expenses.",
  },
  {
    id: "05",
    startAt: 44,
    duration: 10,
    kicker: "step 5  ·  the live campaign page",
    title: "Verified. Live. Ready to share.",
    subtitle:
      "This is what donors see once the admin queue clears the profile.",
  },
  {
    id: "06",
    startAt: 54,
    duration: 6,
    kicker: null,
    title: "Aunties and uncles, paying it forward.",
    subtitle: null,
  },
];

// Zohran's canonical wizard values — used by multiple acts.
const ZOHRAN = {
  fullName: "Zohran M.",
  email: "zohran.m@berkeley.edu",
  school: "UC Berkeley",
  program: "International Relations",
  gradYear: "2027",
  goal: "2000",
  category: "Study abroad",
  bio: "First-generation student. Passionate about diplomacy and human rights.",
  funding:
    "I'm heading to Nairobi this spring for a semester on global governance and civic institutions. My program fee, visa, and travel aren't covered by aid. A grant from My Rich Aunt would close the gap so I can focus on the coursework instead of the bill.",
  location: "Berkeley, CA",
  headline: "First-gen international relations student, headed to Nairobi",
};

export default function DemoStory() {
  useNoIndex();

  const timeline = useReelTimeline(ACTS, TOTAL_SECONDS);
  const { elapsed, ended, currentAct, actProgress, restart } = timeline;

  return (
    <main
      data-testid="demo-story-page"
      className="relative min-h-screen overflow-hidden bg-sand text-ink"
    >
      {/* Warm ambient tint — matches the hero band's warm-sand mood */}
      <span
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.05]"
        style={{
          backgroundImage:
            "radial-gradient(circle at 20% 20%, rgba(253,208,6,0.45) 0, transparent 45%), radial-gradient(circle at 80% 80%, rgba(253,97,6,0.35) 0, transparent 40%)",
        }}
      />
      {/* Repeating brand-mark pattern watermark — same treatment as the
          hero band uses (Hero.jsx). Tiled full-screen at 3.5% opacity
          with a multiply blend so it reads as a whisper of brand
          behind every scene, never competing with the acts. */}
      <div
        aria-hidden
        data-testid="demo-story-pattern-watermark"
        className="pointer-events-none absolute inset-0 opacity-[0.035] mix-blend-multiply"
        style={{
          backgroundImage: `url(${IMAGES.pattern})`,
          backgroundSize: "460px",
          backgroundRepeat: "repeat",
        }}
      />

      {/* Top chrome */}
      <header className="relative z-20 flex items-center justify-between px-6 py-5 lg:px-10">
        <Link
          to="/demo"
          data-testid="demo-story-exit"
          className="text-[10px] font-semibold uppercase tracking-[0.28em] text-olive transition hover:text-ink"
        >
          ← back to /demo
        </Link>
        <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.28em] text-olive">
          <span>Board demo</span>
          <span className="h-1 w-1 rounded-full bg-ink/25" />
          <span>Student experience</span>
          <span className="h-1 w-1 rounded-full bg-ink/25" />
          <span data-testid="demo-story-scene-index">
            {currentAct.id} / {String(ACTS.length).padStart(2, "0")}
          </span>
        </div>
      </header>

      {/* Stage */}
      <section className="relative z-10 mx-auto flex min-h-[calc(100vh-9rem)] max-w-7xl items-center px-6 pb-24 lg:px-10">
        <AnimatePresence mode="wait">
          {currentAct.id === "06" ? (
            <motion.div
              key="end-slate"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -18 }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              className="w-full"
              data-testid={`demo-story-act-${currentAct.id}`}
            >
              <DemoEndSlate onReplay={restart} />
            </motion.div>
          ) : (
            <motion.div
              key={currentAct.id}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -18 }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              className="grid w-full grid-cols-1 items-center gap-8 lg:grid-cols-[1.35fr_1fr] lg:gap-14"
              data-testid={`demo-story-act-${currentAct.id}`}
            >
              <SceneVisual act={currentAct} actProgress={actProgress} />
              <SceneCopy act={currentAct} ended={ended} onReplay={restart} />
            </motion.div>
          )}
        </AnimatePresence>
      </section>

      {/* Controls */}
      <DemoPlayerBar acts={ACTS} totalSeconds={TOTAL_SECONDS} timeline={timeline} testid="demo-story" />

      {/* Progress bar */}
      <div
        aria-hidden
        className="absolute bottom-0 left-0 right-0 z-10 h-[3px] bg-ink/10"
      >
        <div
          data-testid="demo-story-progress"
          className="h-full bg-yellow"
          style={{
            width: `${Math.min(100, (elapsed / TOTAL_SECONDS) * 100)}%`,
            transition: "width 60ms linear",
          }}
        />
      </div>

      <p className="sr-only" aria-live="polite">
        {currentAct.title}
      </p>
    </main>
  );
}

/* ─── Shared primitives ─────────────────────────────────────────────── */

// A device chrome that wraps every scene visual so acts feel like screens.
const BrowserChrome = ({ children, url = "myrichaunt.org/create" }) => (
  <div className="relative w-full overflow-hidden rounded-2xl bg-bone text-ink shadow-[0_40px_100px_-40px_rgba(0,0,0,0.55)]">
    <div className="flex items-center gap-1.5 border-b border-border bg-sand/70 px-4 py-3">
      <span className="h-2.5 w-2.5 rounded-full bg-ink/20" />
      <span className="h-2.5 w-2.5 rounded-full bg-ink/20" />
      <span className="h-2.5 w-2.5 rounded-full bg-ink/20" />
      <span className="ml-3 text-[10px] font-semibold uppercase tracking-[0.22em] text-olive">
        {url}
      </span>
    </div>
    {children}
  </div>
);

// A section card mimicking the wizard's Section component (colored left stripe)
const StepCard = ({ accent = "bg-cobalt", title, subtitle, chip, children }) => (
  <div className="relative overflow-hidden rounded-md border border-border bg-bone">
    <span className={`absolute inset-y-0 left-0 w-1.5 ${accent}`} />
    <div className="p-5 pl-7 lg:p-6 lg:pl-8">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h4 className="font-serif text-xl leading-tight text-ink lg:text-2xl">
            {title}
          </h4>
          {subtitle && (
            <p className="mt-1 text-[13px] text-olive">{subtitle}</p>
          )}
        </div>
        {chip}
      </div>
      <div className="mt-5">{children}</div>
    </div>
  </div>
);

// A typing input field. `progress` (0..1) controls how much of `value` shows.
const TypedInput = ({ label, value, progress, placeholder, suffix, className = "" }) => {
  const shownChars = Math.max(0, Math.round(value.length * clamp(progress, 0, 1)));
  const shown = value.slice(0, shownChars);
  const done = shownChars >= value.length && value.length > 0;
  return (
    <label className={`block ${className}`}>
      <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-olive">
        {label}
      </span>
      <div className="mt-2 flex h-10 w-full items-center rounded-md border border-border bg-sand/40 px-3 text-sm text-ink">
        <span className="flex-1 truncate">
          {shown || <span className="text-ink/30">{placeholder}</span>}
          {progress > 0 && !done && (
            <span
              aria-hidden
              className="ml-0.5 inline-block h-4 w-[2px] translate-y-[3px] animate-pulse bg-tangerine"
            />
          )}
        </span>
        {suffix && done && suffix}
      </div>
    </label>
  );
};

const clamp = (n, lo, hi) => Math.min(hi, Math.max(lo, n));

/* ─── Scene switch ──────────────────────────────────────────────────── */

const SceneVisual = ({ act, actProgress }) => {
  switch (act.id) {
    case "01": return <ActPersonalDetails actProgress={actProgress} />;
    case "02": return <ActPhotosVideo actProgress={actProgress} />;
    case "03": return <ActFundingNeed actProgress={actProgress} />;
    case "04": return <ActAttestations actProgress={actProgress} />;
    case "05": return <ActLivePage actProgress={actProgress} />;
    default: return null;
  }
};

const SceneCopy = ({ act, ended, onReplay }) => (
  <div className="relative">
    {act.kicker && (
      <p
        data-testid={`demo-story-kicker-${act.id}`}
        className="text-[11px] font-semibold uppercase tracking-[0.32em] text-tangerine"
      >
        {act.kicker}
      </p>
    )}
    <h1
      data-testid={`demo-story-title-${act.id}`}
      className={`${act.kicker ? "mt-4" : ""} font-serif text-3xl leading-[1.05] tracking-tight text-ink lg:text-5xl`}
    >
      {act.title}
    </h1>
    {act.subtitle && (
      <p
        data-testid={`demo-story-subtitle-${act.id}`}
        className="mt-5 max-w-xl text-[16px] leading-relaxed text-ink/70"
      >
        {act.subtitle}
      </p>
    )}
    {(act.id === "06" || ended) && (
      <div className="mt-9 flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={onReplay}
          data-testid="demo-story-replay"
          className="inline-flex items-center gap-2 rounded-full bg-ink px-6 py-3 text-sm font-semibold text-sand transition hover:bg-forest"
        >
          <RotateCcw className="h-4 w-4" strokeWidth={2.2} />
          Play again
        </button>
        <Link
          to="/demo"
          data-testid="demo-story-back-to-demo"
          className="inline-flex items-center gap-2 rounded-full border border-ink/25 bg-bone px-6 py-3 text-sm font-semibold text-ink transition hover:bg-beige"
        >
          Explore the platform
          <ArrowRight className="h-4 w-4" strokeWidth={2} />
        </Link>
      </div>
    )}
  </div>
);

/* ─── Act 01: Personal details ──────────────────────────────────────── */

const ActPersonalDetails = ({ actProgress }) => {
  // Stagger each field's typing over the act duration
  const t = actProgress; // 0..1
  const fieldTime = (start, end) => clamp((t - start) / (end - start), 0, 1);

  const eduBadgeVisible = fieldTime(0.32, 0.42) > 0.4;

  return (
    <BrowserChrome url="myrichaunt.org/create">
      <div className="p-5 lg:p-6">
        <div className="mb-4 inline-flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-ink">
          <img
            src={IMAGES.cap}
            alt=""
            className="h-5 w-5 object-contain"
            style={{ mixBlendMode: "multiply" }}
          />
          Pilot preview
        </div>
        <h3 className="font-serif text-2xl leading-tight text-ink lg:text-3xl">
          Student profile
        </h3>
        <p className="mt-1 text-[13px] text-olive">
          Set up your profile. It's what donors see when they support your campaign.
        </p>

        <div className="mt-5">
          <StepCard
            accent="bg-cobalt"
            title="Personal details"
            subtitle="Tell us a bit about you and your school."
          >
            <div className="grid grid-cols-2 gap-3.5">
              <TypedInput
                label="Full name"
                value={ZOHRAN.fullName}
                progress={fieldTime(0.02, 0.16)}
                placeholder="Alice Smith"
              />
              <TypedInput
                label="Email address"
                value={ZOHRAN.email}
                progress={fieldTime(0.16, 0.4)}
                placeholder="you@your-school.edu"
                suffix={
                  <motion.span
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{
                      opacity: eduBadgeVisible ? 1 : 0,
                      scale: eduBadgeVisible ? 1 : 0.8,
                    }}
                    transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                    className="ml-2 inline-flex items-center gap-1 rounded-full bg-grass/15 px-2 py-0.5 text-[9px] font-semibold uppercase tracking-[0.14em] text-grass ring-1 ring-inset ring-grass/30"
                  >
                    <ShieldCheck className="h-2.5 w-2.5" strokeWidth={2.4} /> .edu
                  </motion.span>
                }
              />
              <TypedInput
                label="School"
                value={ZOHRAN.school}
                progress={fieldTime(0.4, 0.55)}
                placeholder="Start typing your school…"
              />
              <TypedInput
                label="Program / Major"
                value={ZOHRAN.program}
                progress={fieldTime(0.55, 0.7)}
                placeholder="Computer Science"
              />
              <TypedInput
                label="Graduation year"
                value={ZOHRAN.gradYear}
                progress={fieldTime(0.7, 0.8)}
                placeholder="2026"
              />
              <TypedInput
                label="Location"
                value={ZOHRAN.location}
                progress={fieldTime(0.8, 0.95)}
                placeholder="City, State"
              />
            </div>
          </StepCard>
        </div>
      </div>
    </BrowserChrome>
  );
};

/* ─── Act 02: Photos + video ─────────────────────────────────────────── */

const ActPhotosVideo = ({ actProgress }) => {
  const t = actProgress;
  const photoIn = (i) => t > 0.15 + i * 0.11;
  const videoIn = t > 0.68;

  const galleryPhotos = [IMAGES.group, IMAGES.campus, IMAGES.friendsTable, IMAGES.walking];

  return (
    <BrowserChrome url="myrichaunt.org/create · media">
      <div className="p-5 lg:p-6">
        {/* Profile photo row */}
        <StepCard
          accent="bg-blossom"
          title="Profile photo & bio"
          subtitle="Add a photo, headline, and a short bio donors will read."
        >
          <div className="flex items-center gap-5">
            <div className="relative h-20 w-20 shrink-0 overflow-hidden rounded-full bg-cobalt">
              <motion.img
                src={IMAGES.group}
                alt=""
                initial={{ opacity: 0, scale: 1.1 }}
                animate={{
                  opacity: t > 0.05 ? 1 : 0,
                  scale: t > 0.05 ? 1 : 1.1,
                }}
                transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
                className="h-full w-full object-cover"
              />
              {t <= 0.05 && (
                <User className="absolute inset-0 m-auto h-8 w-8 text-sand" strokeWidth={1.5} />
              )}
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 rounded-md border border-border bg-sand/40 px-3 py-1.5">
                <Camera className="h-3.5 w-3.5 text-tangerine" strokeWidth={2} />
                <span className="text-xs font-medium text-ink">
                  IMG_2482.jpg
                </span>
                <span className="ml-auto text-[10px] font-semibold uppercase tracking-[0.16em] text-grass">
                  Uploaded
                </span>
              </div>
              <TypedInput
                className="mt-3"
                label="Headline"
                value={ZOHRAN.headline}
                progress={clamp((t - 0.15) / 0.35, 0, 1)}
                placeholder="A first-gen student headed abroad"
              />
            </div>
          </div>
        </StepCard>

        {/* Photos gallery */}
        <div className="mt-4">
          <StepCard
            accent="bg-tangerine"
            title="Photos"
            subtitle="Add photos that help donors get to know you."
          >
            <div className="grid grid-cols-4 gap-2.5">
              {galleryPhotos.map((src, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 12, scale: 0.9 }}
                  animate={{
                    opacity: photoIn(i) ? 1 : 0,
                    y: photoIn(i) ? 0 : 12,
                    scale: photoIn(i) ? 1 : 0.9,
                  }}
                  transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                  className="aspect-square overflow-hidden rounded-md border border-border"
                >
                  <img src={src} alt="" className="h-full w-full object-cover" />
                </motion.div>
              ))}
            </div>
          </StepCard>
        </div>

        {/* Video pitch */}
        <div className="mt-4">
          <StepCard
            accent="bg-scarlet"
            title="Video pitch (optional)"
            subtitle="A short intro helps donors connect."
          >
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{
                opacity: videoIn ? 1 : 0,
                y: videoIn ? 0 : 12,
              }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="overflow-hidden rounded-md border border-border"
            >
              <div className="relative aspect-video w-full bg-ink">
                <img
                  src={IMAGES.group}
                  alt=""
                  className="h-full w-full object-cover opacity-70"
                />
                <span className="absolute inset-0 flex items-center justify-center">
                  <span className="flex h-14 w-14 items-center justify-center rounded-full bg-sand/90 text-ink shadow-lg">
                    <Play className="h-6 w-6 translate-x-[1px]" strokeWidth={2.2} />
                  </span>
                </span>
              </div>
              <div className="flex items-center justify-between bg-bone px-4 py-2.5">
                <div className="flex items-center gap-2">
                  <Video className="h-3.5 w-3.5 text-tangerine" strokeWidth={2} />
                  <span className="text-xs text-olive">zohran-pitch.mp4</span>
                </div>
                <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-grass">
                  Uploaded
                </span>
              </div>
            </motion.div>
          </StepCard>
        </div>
      </div>
    </BrowserChrome>
  );
};

/* ─── Act 03: Funding need ──────────────────────────────────────────── */

const ActFundingNeed = ({ actProgress }) => {
  const t = actProgress;
  const goalDigits = ZOHRAN.goal;
  const goalShown = goalDigits.slice(
    0,
    Math.round(goalDigits.length * clamp((t - 0.05) / 0.15, 0, 1))
  );
  const categoryChosen = t > 0.28;
  const funding = ZOHRAN.funding;
  const fundingWords = funding.split(" ");
  const fundingShownCount = Math.min(
    fundingWords.length,
    Math.round(clamp((t - 0.42) / 0.55, 0, 1) * fundingWords.length)
  );
  const docsIn = t > 0.86;

  return (
    <BrowserChrome url="myrichaunt.org/create · funding">
      <div className="p-5 lg:p-6">
        <StepCard
          accent="bg-yellow"
          title="Funding need"
          subtitle="Tell us what you need and how much you're raising."
          chip={
            <span className="inline-flex items-center gap-1.5 rounded-full bg-tangerine/12 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-tangerine ring-1 ring-inset ring-tangerine/30">
              <Info className="h-2.5 w-2.5" strokeWidth={2.4} /> Beta launch
            </span>
          }
        >
          {/* Policy strip */}
          <div className="mb-5 flex flex-wrap items-center gap-x-5 gap-y-2 rounded-md border border-tangerine/20 bg-tangerine/[0.05] px-4 py-2.5 text-[11px] text-ink">
            <span>
              Grant cap:{" "}
              <strong className="text-ink">$500</strong>
            </span>
            <span>
              Campaign length:{" "}
              <strong className="text-ink">30 days</strong>
            </span>
            <span>
              Disbursement:{" "}
              <strong className="text-ink">weekly, equal installments</strong>
            </span>
          </div>

          <div className="grid grid-cols-2 gap-3.5">
            <label className="block">
              <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-olive">
                Goal amount (USD)
              </span>
              <div className="mt-2 flex h-10 items-center rounded-md border border-border bg-sand/40 px-3 text-sm text-ink">
                <span className="text-ink/40">$&nbsp;</span>
                <span>{goalShown || <span className="text-ink/30">1000</span>}</span>
                {t > 0.05 && goalShown.length < goalDigits.length && (
                  <span className="ml-0.5 inline-block h-4 w-[2px] translate-y-[3px] animate-pulse bg-tangerine" />
                )}
              </div>
            </label>

            <label className="block">
              <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-olive">
                Type of need
              </span>
              <motion.div
                animate={{
                  borderColor: categoryChosen ? "rgb(3, 34, 244)" : "rgb(230, 217, 195)",
                  backgroundColor: categoryChosen ? "rgba(3, 34, 244, 0.04)" : "rgba(237, 228, 211, 0.4)",
                }}
                transition={{ duration: 0.35 }}
                className="mt-2 flex h-10 items-center justify-between rounded-md border px-3 text-sm text-ink"
              >
                <span className={categoryChosen ? "font-medium text-ink" : "text-ink/30"}>
                  {categoryChosen ? ZOHRAN.category : "Choose a category"}
                </span>
                {categoryChosen && (
                  <Check className="h-3.5 w-3.5 text-cobalt" strokeWidth={2.4} />
                )}
              </motion.div>
            </label>
          </div>

          {/* Funding description */}
          <div className="mt-4">
            <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-olive">
              Funding need description
            </span>
            <div className="mt-2 min-h-[110px] rounded-md border-2 border-tangerine/30 bg-sand/30 p-3 text-[13px] leading-relaxed text-ink">
              {fundingShownCount > 0 ? (
                <>
                  {fundingWords.slice(0, fundingShownCount).join(" ")}
                  {fundingShownCount < fundingWords.length && (
                    <span className="ml-0.5 inline-block h-3.5 w-[2px] translate-y-[3px] animate-pulse bg-tangerine" />
                  )}
                </>
              ) : (
                <span className="text-ink/30">
                  Tell us what you'll use the money for…
                </span>
              )}
            </div>
          </div>

          {/* Optional docs — Zohran's $2000 is above the $500 threshold so docs appear */}
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{
              opacity: docsIn ? 1 : 0,
              y: docsIn ? 0 : 8,
            }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="mt-4 rounded-md border border-cobalt/30 bg-cobalt/[0.05] p-3"
          >
            <div className="flex items-center gap-2">
              <FileText className="h-3.5 w-3.5 text-cobalt" strokeWidth={2} />
              <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-cobalt">
                Supporting documentation attached
              </span>
            </div>
            <ul className="mt-2 space-y-1 text-xs text-ink">
              <li className="flex items-center gap-1.5">
                <FileText className="h-3 w-3 text-cobalt" strokeWidth={2} />
                nairobi-program-invoice.pdf
              </li>
              <li className="flex items-center gap-1.5">
                <FileText className="h-3 w-3 text-cobalt" strokeWidth={2} />
                travel-visa-quote.pdf
              </li>
            </ul>
          </motion.div>

          <div className="mt-4 flex items-center justify-between">
            <span className="inline-flex items-center gap-1.5 text-[11px] text-olive">
              <Link2 className="h-3 w-3" strokeWidth={2} />
              Stripe payout account · connected
            </span>
          </div>
        </StepCard>
      </div>
    </BrowserChrome>
  );
};

/* ─── Act 04: Attestations ──────────────────────────────────────────── */

const ActAttestations = ({ actProgress }) => {
  const t = actProgress;
  const rows = [
    {
      testid: "eligibility",
      label: "Campaign eligibility attestation",
      body:
        "I am the enrolled student named above and the intended beneficiary of this campaign. I am personally creating and submitting this campaign.",
      wrapCls: "border-scarlet/40 bg-scarlet/[0.04]",
      boxFill: "rgb(232,66,29)",
      boxBorder: "rgb(232,66,29)",
      checkAt: 0.18,
    },
    {
      testid: "consent",
      label: "I consent to enrollment verification",
      body:
        "I authorize My Rich Aunt to verify my current enrollment through the National Student Clearinghouse.",
      wrapCls: "border-cobalt/40 bg-cobalt/[0.04]",
      boxFill: "rgb(3,34,244)",
      boxBorder: "rgb(3,34,244)",
      checkAt: 0.45,
    },
    {
      testid: "student-cert",
      label: "Student certification",
      body:
        "I certify that grant funds will be used exclusively for education-related and student living costs not already covered by my financial aid.",
      wrapCls: "border-tangerine/40 bg-tangerine/[0.04]",
      boxFill: "rgb(253,97,6)",
      boxBorder: "rgb(253,97,6)",
      checkAt: 0.72,
    },
  ];
  const allChecked = t > 0.86;
  const checkedCount = rows.filter((r) => t > r.checkAt).length;

  return (
    <BrowserChrome url="myrichaunt.org/create · attestations">
      <div className="p-5 lg:p-6">
        <div className="rounded-md border border-border bg-bone/40 p-5">
          <div className="flex items-baseline justify-between border-b border-border/70 pb-3">
            <span className="text-[11px] font-semibold uppercase tracking-[0.22em] text-ink">
              Attestations
            </span>
            <motion.span
              animate={{
                backgroundColor: allChecked ? "rgba(45,180,49,0.15)" : "rgba(45,180,49,0)",
                color: allChecked ? "rgb(45,180,49)" : "rgb(115,116,88)",
              }}
              transition={{ duration: 0.35 }}
              className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] ring-1 ring-inset"
              style={{ borderColor: allChecked ? "rgba(45,180,49,0.3)" : "rgba(45,180,49,0)" }}
            >
              {allChecked ? (
                <>
                  <CircleCheck className="h-3 w-3" strokeWidth={2.4} /> All 3 complete
                </>
              ) : (
                <>{checkedCount} of 3 required</>
              )}
            </motion.span>
          </div>

          <div className="mt-4 space-y-3">
            {rows.map((row) => {
              const checked = t > row.checkAt;
              return (
                <div
                  key={row.testid}
                  className={`flex items-start gap-3 rounded-md border p-4 ${row.wrapCls}`}
                >
                  <motion.span
                    animate={{
                      backgroundColor: checked ? row.boxFill : "rgb(255,255,255)",
                      borderColor: checked ? row.boxBorder : "rgb(200,190,170)",
                    }}
                    transition={{ duration: 0.3 }}
                    className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border-2"
                  >
                    <motion.span
                      initial={{ scale: 0 }}
                      animate={{ scale: checked ? 1 : 0 }}
                      transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
                    >
                      <Check className="h-3 w-3 text-bone" strokeWidth={3.2} />
                    </motion.span>
                  </motion.span>
                  <div className="min-w-0">
                    <p className="text-[13px] font-semibold text-ink">{row.label}</p>
                    <p className="mt-1 text-[12px] leading-relaxed text-olive">
                      {row.body}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Submit CTA emphasizes on completion */}
          <motion.div
            animate={{ opacity: allChecked ? 1 : 0.4 }}
            transition={{ duration: 0.4 }}
            className="mt-5"
          >
            <span className="inline-flex items-center gap-2 rounded-full bg-yellow px-4 py-2 text-xs font-semibold text-ink">
              <ArrowRight className="h-3.5 w-3.5" strokeWidth={2.4} />
              Submit profile for review
            </span>
          </motion.div>
        </div>
      </div>
    </BrowserChrome>
  );
};

/* ─── Act 05: Live campaign page ─────────────────────────────────────── */

const ActLivePage = ({ actProgress }) => {
  const t = actProgress;
  // Simulate donations landing during the act
  const raised = Math.round(clamp(t / 0.85, 0, 1) * 2000);
  const pct = Math.min(100, Math.round((raised / 2000) * 100));
  const supporters = Math.round(clamp(t / 0.9, 0, 1) * 34);

  return (
    <BrowserChrome url="myrichaunt.org/campaign/zohran">
      <div className="grid grid-cols-1 gap-5 p-5 lg:grid-cols-[1.35fr_1fr] lg:p-6">
        {/* Left — hero + story */}
        <div>
          <div className="relative aspect-[16/10] w-full overflow-hidden rounded-md">
            <img
              src={IMAGES.group}
              alt=""
              className="h-full w-full object-cover"
            />
            <span
              aria-hidden
              className="pointer-events-none absolute inset-0 bg-gradient-to-t from-ink/40 via-transparent"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.7, rotate: -14 }}
              animate={{
                opacity: t > 0.06 ? 1 : 0,
                scale: t > 0.06 ? 1 : 0.7,
                rotate: t > 0.06 ? -8 : -14,
              }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="absolute right-4 top-4 inline-flex items-center gap-1.5 rounded-full border-2 border-grass bg-bone px-2.5 py-1 text-[9px] font-bold uppercase tracking-[0.2em] text-grass shadow-md"
            >
              <Check className="h-3 w-3" strokeWidth={2.8} /> Verified &amp; live
            </motion.div>
          </div>

          <div className="mt-4">
            <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-tangerine">
              Study abroad
            </span>
            <h3 className="mt-1 font-serif text-2xl leading-tight text-ink lg:text-3xl">
              Nairobi, or bust.
            </h3>
            <p className="mt-2 flex items-center gap-1.5 text-[12px] text-olive">
              <MapPin className="h-3 w-3" strokeWidth={2} />
              Zohran M. · UC Berkeley '27, International Relations
            </p>
            <p className="mt-3 text-[13px] leading-relaxed text-ink/85">
              {ZOHRAN.funding}
            </p>
          </div>
        </div>

        {/* Right — Progress card */}
        <div>
          <div className="rounded-md border border-border bg-sand/40 p-5">
            <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-olive">
              Progress
            </span>
            <p className="mt-2 font-serif text-4xl leading-none text-ink">
              ${raised.toLocaleString()}
            </p>
            <p className="mt-1 text-[12px] text-olive">
              raised of{" "}
              <span className="font-semibold text-ink">$2,000</span> goal
            </p>

            <div className="mt-4 h-2 w-full overflow-hidden rounded-full bg-ink/10">
              <div
                className="h-full bg-tangerine"
                style={{
                  width: `${pct}%`,
                  transition: "width 120ms linear",
                }}
              />
            </div>

            <div className="mt-4 grid grid-cols-2 gap-2 text-[11px]">
              <div className="rounded-md bg-bone p-2.5">
                <p className="text-olive">Progress</p>
                <p className="mt-0.5 font-semibold text-ink">{pct}%</p>
              </div>
              <div className="rounded-md bg-bone p-2.5">
                <p className="text-olive">Days left</p>
                <p className="mt-0.5 font-semibold text-ink">14 days</p>
              </div>
            </div>

            {/* Supporters row */}
            <div className="mt-4 flex items-center gap-2">
              <div className="flex -space-x-2">
                {[IMAGES.student1, IMAGES.student2, IMAGES.founder].map((s, i) => (
                  <span
                    key={i}
                    className="h-6 w-6 overflow-hidden rounded-full border-2 border-sand bg-bone"
                  >
                    <img src={s} alt="" className="h-full w-full object-cover" />
                  </span>
                ))}
                <span className="flex h-6 w-6 items-center justify-center rounded-full border-2 border-sand bg-ink text-[9px] font-bold text-sand">
                  +{Math.max(0, supporters - 3)}
                </span>
              </div>
              <p className="text-[11px] text-olive">
                <span className="font-semibold text-ink">{supporters}</span> donors this week
              </p>
            </div>

            <button
              type="button"
              className="mt-4 flex w-full items-center justify-center gap-2 rounded-full bg-ink px-5 py-2.5 text-[12px] font-semibold text-sand"
            >
              <Heart className="h-3.5 w-3.5" strokeWidth={2} />
              Donate now
            </button>
          </div>
        </div>
      </div>
    </BrowserChrome>
  );
};

/* ─── End of file — Act 06 is rendered via <DemoEndSlate /> at the top-level
       conditional in the reel's <section>. See imports above. ─────────── */
